<template>
    <div class="gap-xs relative z-20 flex w-full flex-col">
        <div v-if="!isExpanded && collapsable" class="input-wrapper flex items-center">
            <input
                type="text"
                :placeholder="placeholder"
                class="flex-1 border-none bg-transparent outline-none"
                @focus="expandComposer"
                @click="expandComposer"
            />
        </div>

        <form
            v-else
            class="gap-xs flex w-full flex-col"
            @submit.prevent="handleComment"
            @keydown="handleCommentKeydown"
        >
            <div
                class="input-wrapper flex-col items-stretch"
                data-file-upload-root
                :class="{ 'input-error': errorPool.comment }"
                @click="focusEditor"
            >
                <Editor
                    ref="editorRef"
                    v-model="commentInput.text"
                    editor-style="min-height: 120px; width: 100%; border: none;"
                    :placeholder="placeholder"
                    @blur="handleEditorBlur"
                    @load="handleEditorLoad"
                    @text-change="handleEditorTextChange"
                >
                    <template #toolbar>
                        <div class="comment-toolbar">
                            <div class="comment-toolbar__controls">
                                <span class="ql-formats">
                                    <select class="ql-header">
                                        <option selected>Normal text</option>
                                        <option value="1">Heading 1</option>
                                        <option value="2">Heading 2</option>
                                    </select>
                                </span>

                                <span class="ql-formats">
                                    <button class="ql-bold" type="button"></button>
                                    <button class="ql-italic" type="button"></button>
                                    <button class="ql-underline" type="button"></button>
                                    <button class="ql-strike" type="button"></button>
                                    <select class="ql-color">
                                        <option selected></option>
                                        <option value="#1a1a1a"></option>
                                        <option value="#1d4ed8"></option>
                                        <option value="#0e7490"></option>
                                        <option value="#155c4a"></option>
                                        <option value="#c2410c"></option>
                                        <option value="#991b1b"></option>
                                        <option value="#6d28d9"></option>
                                        <option value="#6b7280"></option>
                                        <option value="#3b82f6"></option>
                                        <option value="#06b6d4"></option>
                                        <option value="#10b981"></option>
                                        <option value="#f59e0b"></option>
                                        <option value="#ef4444"></option>
                                        <option value="#a855f7"></option>
                                        <option value="#ffffff"></option>
                                        <option value="#dbeafe"></option>
                                        <option value="#cffafe"></option>
                                        <option value="#d1fae5"></option>
                                        <option value="#fef08a"></option>
                                        <option value="#fce7f3"></option>
                                        <option value="#ede9fe"></option>
                                    </select>
                                </span>

                                <span class="ql-formats">
                                    <button class="ql-list" value="bullet" type="button"></button>
                                    <button class="ql-list" value="ordered" type="button"></button>
                                </span>
                            </div>

                            <div class="comment-toolbar__actions">
                                <span class="ql-formats">
                                    <button class="ql-link" type="button"></button>
                                    <button class="ql-image" type="button"></button>
                                </span>
                                <!-- Surface-specific toolbar controls (e.g. visibility toggle) -->
                                <slot name="toolbar-extras" />
                            </div>
                        </div>

                        <!-- Surface-specific second toolbar row, directly under the formatting line
                             (e.g. the NOC audience toggle). Separated from the text area by the
                             toolbar's bottom border. -->
                        <div v-if="$slots['toolbar-secondary']" class="comment-toolbar__secondary">
                            <slot name="toolbar-secondary" />
                        </div>
                    </template>
                </Editor>

                <input
                    ref="inlineImageInputRef"
                    type="file"
                    class="hidden"
                    accept="image/*"
                    multiple
                    @change="handleInlineImageUpload"
                />
            </div>

            <div>
                <FileUpload
                    v-model:uploaded-files="commentInput.attachments"
                    :max-files="MAX_FILES"
                    @upload="handleAttachmentUpload"
                    @remove="handleRemove($event)"
                />
            </div>

            <p v-if="errorPool.comment" class="text-error ml-auto">
                {{ errorPool.comment }}
            </p>

            <div class="flex items-center justify-end">
                <!-- Surface-specific footer hint (e.g. "Visible to all customers") -->
                <div v-if="$slots['footer-hint']" class="mr-auto min-w-0 pr-2">
                    <slot name="footer-hint" />
                </div>
                <button
                    v-if="collapsable"
                    type="button"
                    class="btn-link mr-sm mt-2 font-bold text-(--text-muted) no-underline"
                    @click="handleCancel"
                >
                    Cancel
                </button>
                <Button type="submit" class="mt-2 flex shrink-0 items-center justify-center whitespace-nowrap">
                    <span v-if="!isProcessing">{{ submitLabel }}</span>
                    <i v-else class="i-spinner-white animate-spin" />
                </Button>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { nextTick, onMounted, ref, watch } from 'vue';
import Editor from 'primevue/editor';
import FileUpload from '@/components/support/file-upload.vue';
import { useOrganizationStore } from '@/stores/organization';
import {
    hasIncidentCommentEditorText,
    normalizeIncidentCommentEditorHtml,
    serializeIncidentCommentEditorToJiraWiki,
    type IncidentCommentDraft,
    type IncidentCommentPayload,
} from '@/lib/incident-comment-editor';
import { normalizeJiraWikiText } from '@/lib/jira-wiki';
import {
    markInlineAttachmentElement,
    removeInlineAttachmentElementsForFile,
    syncComposerAttachmentsWithEditor,
} from '@/lib/incident-comment-inline-attachments';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 200 * 1024 * 1024;
const MAX_COMMENT_LENGTH = 10000;

const props = withDefaults(
    defineProps<{
        collapsable?: boolean;
        isProcessing?: boolean;
        submissionSucceeded?: boolean;
        placeholder?: string;
        submitLabel?: string;
    }>(),
    {
        collapsable: false,
        isProcessing: false,
        submissionSucceeded: false,
        placeholder: 'Add a comment',
        submitLabel: 'Submit',
    },
);

const emit = defineEmits<{
    (e: 'submit', payload: IncidentCommentPayload): void;
    (e: 'cancel'): void;
}>();

const organizationStore = useOrganizationStore();
const isExpanded = ref(!props.collapsable);
const commentInput = ref({ text: '', attachments: [] as File[] });
const errorPool = ref<Record<string, string>>({});
const inlineImageInputRef = ref<HTMLInputElement | null>(null);
const editorRef = ref<InstanceType<typeof Editor> | null>(null);

type EditorInstance = InstanceType<typeof Editor> & {
    $el?: Element;
    quill?: unknown;
    getQuill?: () => unknown;
};

function getQuillInstance() {
    const editor = editorRef.value as EditorInstance | null;
    if (!editor) return null;
    return typeof editor.getQuill === 'function' ? editor.getQuill() : editor.quill;
}

function getQuillApi() {
    return getQuillInstance() as {
        root?: HTMLElement;
        container?: HTMLElement;
        theme?: { tooltip?: { boundsContainer?: HTMLElement } };
        focus?: () => void;
        getLength?: () => number;
        getModule?: (name: string) => { addHandler?: (name: string, handler: () => void) => void } | undefined;
        getSelection?: (focus?: boolean) => { index: number; length: number } | null;
        format?: (name: string, value: unknown, source?: string) => void;
        insertEmbed?: (index: number, type: string, value: string, source?: string) => void;
        setSelection?: (index: number, length?: number, source?: string) => void;
        setText?: (text: string, source?: string) => void;
    } | null;
}

function getEditorEditableElement(): HTMLElement | null {
    const quill = getQuillApi();
    if (quill?.root instanceof HTMLElement) {
        return quill.root;
    }

    const editor = editorRef.value as EditorInstance | null;
    if (!(editor?.$el instanceof Element)) {
        return null;
    }

    return editor.$el.querySelector<HTMLElement>('.ql-editor, [contenteditable="true"], textarea, [data-editor-input]');
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
                return;
            }

            reject(new Error('Failed to read image preview'));
        };
        reader.onerror = () => {
            reject(reader.error ?? new Error('Failed to read image preview'));
        };
        reader.readAsDataURL(file);
    });
}

function syncCommentInputFromEditor() {
    const quill = getQuillApi();
    const root = quill?.root;
    if (!root) return;

    commentInput.value.attachments = syncComposerAttachmentsWithEditor(commentInput.value.attachments, root);
    commentInput.value.text = root.innerHTML;
}

async function insertInlineImages(files: File[]) {
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    const insertedImages = await Promise.all(
        imageFiles.map(async (file) => ({
            file,
            previewUrl: await readFileAsDataUrl(file),
        })),
    );

    nextTick(() => {
        const quill = getQuillApi();
        const root = quill?.root;

        if (!quill || !root || typeof quill.insertEmbed !== 'function') {
            return;
        }

        let index = quill.getSelection?.(true)?.index;
        if (typeof index !== 'number') {
            index = Math.max((quill.getLength?.() ?? 1) - 1, 0);
        }

        for (const { previewUrl } of insertedImages) {
            quill.insertEmbed(index, 'image', previewUrl, 'user');
            index += 1;
            quill.setSelection?.(index, 0, 'silent');
        }

        nextTick(() => {
            for (const { file, previewUrl } of insertedImages) {
                const image = Array.from(root.querySelectorAll('img')).find(
                    (candidate) => candidate.src === previewUrl,
                );
                if (!image) continue;

                markInlineAttachmentElement(image, file);
            }

            syncCommentInputFromEditor();
            syncEditorHeight();
        });
    });
}

function focusEditor(event?: Event) {
    // Clicks landing inside the editable content (or toolbar/controls) must keep the caret where
    // the user clicked — only clicks on the surrounding wrapper padding should focus-and-move-to-end.
    // Without `.ql-editor` here, clicking mid-sentence would immediately snap the caret to the end.
    if ((event?.target as HTMLElement | null)?.closest('button, input, select, textarea, a, .ql-toolbar, .ql-editor')) {
        return;
    }

    nextTick(() => {
        const quill = getQuillApi();
        const editableElement = getEditorEditableElement();
        const textLength = quill?.getLength?.() ?? 1;

        if (editableElement) {
            editableElement.focus();
            quill?.setSelection?.(Math.max(textLength - 1, 0), 0, 'silent');
            return;
        }

        quill?.focus?.();
    });
}

function setupQuillListeners() {
    nextTick(() => {
        const quill = getQuillApi();
        const root = quill?.root;
        if (!root) return;

        const container = quill?.container;
        const tooltip = quill?.theme?.tooltip;

        if (container && tooltip) {
            tooltip.boundsContainer = container;
        }

        quill.getModule?.('toolbar')?.addHandler?.('image', triggerInlineImageUpload);

        root.removeEventListener('paste', handlePaste, true);
        root.removeEventListener('drop', handleDrop, true);
        root.addEventListener('paste', handlePaste, true);
        root.addEventListener('drop', handleDrop, true);
    });
}

function handleEditorTextChange() {
    syncCommentInputFromEditor();
    syncEditorHeight();
}

function expandComposer() {
    isExpanded.value = true;
    focusEditor();
}

function triggerInlineImageUpload() {
    inlineImageInputRef.value?.click();
}

function resetComposer() {
    commentInput.value = { text: '', attachments: [] as File[] };
    errorPool.value = {};
    // PrimeVue's Editor skips its modelValue→Quill sync while the editor has focus
    // (Editor.vue: `!this.quill.hasFocus()`), so Cmd+Enter submit — which leaves focus
    // inside the editor — would otherwise leave the old text on screen. Clear Quill
    // directly so the visible content always tracks the cleared model.
    getQuillApi()?.setText?.('', 'silent');
}

function addFiles(files: FileList | File[]) {
    const filesArray = Array.from(files);
    const totalFiles = commentInput.value.attachments.length + filesArray.length;

    if (totalFiles > MAX_FILES) {
        organizationStore.setToastMessage(`You can only upload a maximum of ${MAX_FILES} files`, 'warning');
        return false;
    }

    if (filesArray.some((file) => file.size > MAX_FILE_SIZE)) {
        organizationStore.setToastMessage('Each file must be smaller than 200MB', 'warning');
        return false;
    }

    if (filesArray.some((file) => file.size < 1)) {
        organizationStore.setToastMessage('You cannot upload empty attachments', 'warning');
        return false;
    }

    commentInput.value.attachments = [...commentInput.value.attachments, ...filesArray];
    return true;
}

function handleAttachmentUpload(files: File[]) {
    commentInput.value.attachments = files;
}

async function handleInlineImageUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;

    const filesArray = Array.from(target.files).filter((file) => file.type.startsWith('image/'));
    const filesAdded = addFiles(filesArray);

    if (filesAdded) {
        await insertInlineImages(filesArray);
    }

    target.value = '';
}

function handlePaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const files: File[] = [];

    if (clipboardData.files && clipboardData.files.length > 0) {
        files.push(...Array.from(clipboardData.files));
    }

    if (files.length === 0 && clipboardData.items) {
        for (const item of Array.from(clipboardData.items)) {
            if (item.kind !== 'file') continue;
            const file = item.getAsFile();
            if (file) files.push(file);
        }
    }

    if (files.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const filesAdded = addFiles(files);
    if (filesAdded && files.some((file) => file.type.startsWith('image/'))) {
        void insertInlineImages(files);
        organizationStore.setToastMessage('Image uploaded', 'check');
    }
}

function handleDrop(event: DragEvent) {
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const filesArray = Array.from(files);
    const filesAdded = addFiles(filesArray);
    if (filesAdded) {
        void insertInlineImages(filesArray);
    }
}

function handleRemove(index: number) {
    const [removedFile] = commentInput.value.attachments.splice(index, 1);
    if (!removedFile) return;

    const quill = getQuillApi();
    const root = quill?.root;
    if (!root) return;

    removeInlineAttachmentElementsForFile(root, removedFile);
    syncCommentInputFromEditor();
    syncEditorHeight();
}

function handleCancel() {
    resetComposer();
    if (props.collapsable) {
        isExpanded.value = false;
    }
    emit('cancel');
}

function handleEditorBlur() {
    if (!props.collapsable) return;

    window.requestAnimationFrame(() => {
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement?.closest('[data-file-upload-root]')) return;
        if (hasIncidentCommentEditorText(commentInput.value.text) || commentInput.value.attachments.length > 0) return;
        isExpanded.value = false;
    });
}

function handleCommentKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) {
        return;
    }

    event.preventDefault();
    void handleComment();
}

watch(editorRef, (newValue) => {
    if (newValue) {
        setupQuillListeners();
    }
});

watch(isExpanded, (expanded) => {
    if (expanded) {
        setupQuillListeners();
    }
});

// PrimeVue's Editor hands the placeholder to Quill only at init; push later changes (e.g. the NOC
// composer swapping copy when the audience toggles) onto the data-attribute Quill renders from.
watch(
    () => props.placeholder,
    (value) => {
        const root = getQuillApi()?.root;
        if (root && typeof value === 'string') {
            root.dataset.placeholder = value;
        }
    },
);

watch(
    () => [props.isProcessing, props.submissionSucceeded] as const,
    ([newIsProcessing, newSubmissionSucceeded], [oldIsProcessing]) => {
        if (oldIsProcessing === true && newIsProcessing === false && newSubmissionSucceeded) {
            resetComposer();
            if (props.collapsable) {
                isExpanded.value = false;
            }
        }
    },
);

onMounted(() => {
    if (isExpanded.value) {
        setupQuillListeners();
        syncEditorHeight();
    }
});

function handleEditorLoad() {
    setupQuillListeners();
    syncEditorHeight();
    if (isExpanded.value) {
        focusEditor();
    }
}

function syncEditorHeight() {
    nextTick(() => {
        const quill = getQuillInstance() as { root?: HTMLElement } | null;
        const root = quill?.root;
        if (!root) return;

        root.style.height = 'auto';
        root.style.minHeight = '72px';
        root.style.overflowY = 'hidden';
        root.style.maxHeight = 'none';
        root.style.height = `${Math.max(root.scrollHeight, 72)}px`;
    });
}

async function handleComment() {
    if (props.isProcessing) {
        return;
    }

    const normalizedText = normalizeIncidentCommentEditorHtml(commentInput.value.text);
    const submittedText = normalizeJiraWikiText(serializeIncidentCommentEditorToJiraWiki(normalizedText));
    const hasText = hasIncidentCommentEditorText(normalizedText);

    if (!hasText && commentInput.value.attachments.length === 0) {
        errorPool.value = {
            comment: 'You need a comment or an attachment to submit',
        };
        return;
    }

    if (submittedText.length > MAX_COMMENT_LENGTH) {
        errorPool.value = {
            comment: `Comment is too long (max ${MAX_COMMENT_LENGTH.toLocaleString()} characters). You entered ${submittedText.length.toLocaleString()} characters.`,
        };
        return;
    }

    errorPool.value = {};
    emit('submit', {
        text: submittedText,
        attachments: commentInput.value.attachments,
    });
}

// Public API for surface wrappers (e.g. master-broadcast / child-reply composers).
function focus() {
    if (props.collapsable) {
        isExpanded.value = true;
    }
    focusEditor();
}

// Draft snapshot / restore — lets a wrapper preserve unsent text + attachments per target (e.g. the
// NOC composer keeping a separate in-progress draft for each linked ticket). In-memory only.
function getDraft(): IncidentCommentDraft {
    return {
        text: commentInput.value.text,
        attachments: [...commentInput.value.attachments],
    };
}

function setDraft(draft: IncidentCommentDraft | null | undefined) {
    commentInput.value = {
        text: draft?.text ?? '',
        attachments: [...(draft?.attachments ?? [])],
    };
    errorPool.value = {};
    // Once the Editor has pushed the restored HTML into Quill, re-link inline attachments and
    // recompute the auto-grow height for the new content.
    nextTick(() => {
        if (getQuillApi()?.root) syncCommentInputFromEditor();
        syncEditorHeight();
    });
}

function hasDraftContent(): boolean {
    return hasIncidentCommentEditorText(commentInput.value.text) || commentInput.value.attachments.length > 0;
}

defineExpose({ reset: resetComposer, focus, getDraft, setDraft, hasDraftContent });
</script>

<style scoped>
@reference '@/assets/styles/default.css';

/* Second toolbar row (e.g. NOC audience toggle), under the formatting line. */
.comment-toolbar__secondary {
    width: 100%;
    margin-top: 0.375rem;
    padding-top: 0.375rem;
    border-top: 1px solid var(--border-subtle);
}

:deep(.p-editor) {
    width: 100%;
    height: auto;
    border: none;
    box-shadow: none;
}

:deep(.p-editor-toolbar) {
    border: none;
    border-bottom: 1px solid var(--border-subtle);
    background-color: transparent;
    padding: 0.25rem 0.5rem;
}

:deep(.p-editor-content) {
    border: none;
    height: auto;
}

:deep(.ql-container) {
    border: none;
    height: auto;
}

:deep(.ql-editor) {
    min-height: 72px;
    padding: 0.75rem 1rem;
    font-size: var(--text-base);
    line-height: 1.5rem;
    color: var(--text-primary);
    height: auto;
    overflow-y: hidden;
}

:deep(.ql-editor.ql-blank::before) {
    left: 1rem;
    right: 1rem;
}

/* Quill's contenteditable is a text field to iOS Safari: below 16px it
   auto-zooms on focus and the zoom sticks. Same threshold as the global
   touch-device input rule in default.css. */
@media (hover: none) and (pointer: coarse) {
    :deep(.ql-editor) {
        font-size: 16px;
    }
}

:deep(.ql-editor ul) {
    margin-block: 0.5rem;
    padding-left: 1.25rem;
    list-style-type: disc;
}

:deep(.ql-editor a) {
    text-decoration: underline;
}

:deep(.ql-editor ol) {
    margin-block: 0.5rem;
    padding-left: 1.25rem;
    list-style-type: decimal;
}

:deep(.ql-editor ol li[data-list='bullet'])::before {
    content: '•';
    display: inline-flex;
    justify-content: center;
    width: 1rem;
    margin-left: -1rem;
}

:deep(.ql-picker-label) {
    border: none;
    padding-left: 0;
    padding-right: 0;
    font-size: var(--text-xs);
    line-height: var(--leading-xs);
    color: var(--text-muted);
}

:deep(.ql-picker-options) {
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    background-color: var(--overlay-surface);
    box-shadow: var(--ring-popover);
}

:deep(.ql-picker) svg {
    width: 1rem;
    height: 1rem;
    margin: 0;
}

:deep(.ql-formats) {
    margin-right: 0;
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

:deep(.ql-formats button) {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--radius-sm);
    padding: 0.25rem;
    color: var(--text-muted);
    background: none;
    cursor: pointer;
    transition:
        background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
        color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.ql-formats button svg) {
    display: block;
    float: none;
    width: 1rem;
    height: 1rem;
    margin: auto;
}

:deep(.ql-stroke) {
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2;
}

:deep(.ql-stroke-miter) {
    fill: none;
    stroke: currentColor;
    stroke-miterlimit: 10;
    stroke-width: 2;
}

:deep(.ql-fill) {
    fill: currentColor;
    stroke: none;
}

:deep(.ql-even) {
    fill-rule: evenodd;
}

:deep(.ql-thin) {
    stroke-width: 1;
}

:deep(.ql-transparent) {
    opacity: 0.4;
}

:deep(.ql-snow.ql-toolbar button.ql-active),
:deep(.ql-snow .ql-toolbar button.ql-active),
:deep(.ql-snow.ql-toolbar .ql-picker-label.ql-active),
:deep(.ql-snow .ql-toolbar .ql-picker-label.ql-active) {
    border-radius: var(--radius-sm);
    background-color: var(--surface-highlight);
    color: var(--icon-primary);
}

:deep(.ql-snow.ql-toolbar button:hover),
:deep(.ql-snow .ql-toolbar button:hover),
:deep(.ql-snow.ql-toolbar button:focus),
:deep(.ql-snow .ql-toolbar button:focus),
:deep(.ql-snow.ql-toolbar .ql-picker-label:hover),
:deep(.ql-snow .ql-toolbar .ql-picker-label:hover) {
    border-radius: var(--radius-sm);
    background-color: var(--surface-highlight);
    color: var(--icon-primary);
}

:deep(.ql-snow .ql-tooltip) {
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    background-color: var(--overlay-surface);
    padding: 0.75rem;
    box-shadow: var(--ring-popover);
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

:deep(.ql-snow .ql-tooltip.ql-hidden) {
    display: none;
}

:deep(.ql-snow .ql-tooltip::before) {
    display: block;
    margin-bottom: 0.25rem;
    font-family: var(--font-medium);
    font-size: var(--text-xs);
    line-height: var(--leading-xs);
    color: var(--text-muted);
}

:deep(.ql-snow .ql-tooltip input[type='text']) {
    width: 16rem;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    padding: 0.25rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    outline: none;
    line-height: 1.25rem;
}

:deep(.ql-snow .ql-tooltip a.ql-action),
:deep(.ql-snow .ql-tooltip a.ql-remove) {
    display: inline-flex;
    align-items: center;
    font-family: var(--font-bold);
    font-size: var(--text-sm);
    color: var(--link-default);
    text-decoration: none;
    line-height: 1.25rem;
}

:deep(.ql-snow .ql-tooltip a.ql-action:hover),
:deep(.ql-snow .ql-tooltip a.ql-remove:hover) {
    color: var(--link-hover);
}

.comment-toolbar {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    /* Narrow viewports: let the actions group (link/image + slot) drop to a second line instead of
       overlapping the formatting controls. The controls group wraps internally too. */
    flex-wrap: wrap;
}

.comment-toolbar__controls {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.comment-toolbar__actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.25rem;
}

:deep(.comment-toolbar .ql-picker.ql-header) {
    min-width: 7.5rem;
    font-family: var(--font-default);
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-label) {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    border-radius: var(--radius-sm);
    padding: 0.25rem 0.5rem;
    font-size: var(--text-xs);
    line-height: var(--leading-xs);
    color: var(--text-primary);
    font-family: var(--font-default);
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-label::before) {
    font-family: var(--font-default);
    padding: 0;
    line-height: 1;
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-options),
:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-item),
:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-item::before) {
    font-family: var(--font-default);
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-options) {
    min-width: 11rem;
    padding: 0.375rem;
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-item) {
    border-radius: var(--radius-sm);
    padding: 0.5rem 0.75rem;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    min-height: 2.5rem;
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-item::before) {
    display: block;
    width: 100%;
    font-size: 0.9375rem;
    line-height: 1.15;
    font-weight: 400;
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-item[data-value='1']::before) {
    font-family: var(--font-semibold);
    font-size: 1.125rem;
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-item[data-value='2']::before) {
    font-family: var(--font-semibold);
    font-size: 1rem;
}

:deep(.comment-toolbar .ql-picker.ql-header .ql-picker-label svg) {
    position: static;
    inset: auto;
    width: 1rem;
    height: 1rem;
}

:deep(.comment-toolbar .ql-picker.ql-color) {
    font-family: var(--font-default);
    display: inline-flex;
    float: none;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-label) {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    transition:
        background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
        color 150ms cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    height: 100%;
    padding: 0;
    line-height: 0;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-label svg) {
    display: block;
    position: static;
    inset: auto;
    width: 1rem;
    height: 1rem;
    margin: auto;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options) {
    min-width: 15rem;
    padding: 0.75rem;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item) {
    width: 2rem;
    height: 2rem;
    margin: 0.25rem;
    border-radius: 0.375rem;
    border: 1px solid var(--border-subtle);
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:first-child),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='']),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:not([data-value])) {
    position: relative;
    overflow: hidden;
    background: #ffffff !important;
    border-color: #d1d5db;
    border-radius: 0.375rem;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:first-child:hover),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:first-child.ql-selected),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='']:hover),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value=''].ql-selected),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:not([data-value]):hover),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:not([data-value]).ql-selected) {
    background: #ffffff !important;
    border-color: #d1d5db;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:first-child::after),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='']::after),
:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item:not([data-value])::after) {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    background: linear-gradient(
        -45deg,
        transparent calc(50% - 1px),
        #ef4444 calc(50% - 1px),
        #ef4444 calc(50% + 1px),
        transparent calc(50% + 1px)
    );
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#1a1a1a']) {
    border-color: #0f0f0f;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#1d4ed8']) {
    border-color: #1e3a8a;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#0e7490']) {
    border-color: #155e75;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#155c4a']) {
    border-color: #0f4336;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#c2410c']) {
    border-color: #9a3412;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#991b1b']) {
    border-color: #7f1d1d;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#6d28d9']) {
    border-color: #5b21b6;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#6b7280']) {
    border-color: #4b5563;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#3b82f6']) {
    border-color: #2563eb;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#06b6d4']) {
    border-color: #0891b2;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#10b981']) {
    border-color: #059669;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#f59e0b']) {
    border-color: #d97706;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#ef4444']) {
    border-color: #dc2626;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#a855f7']) {
    border-color: #9333ea;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#ffffff']) {
    border-color: #d1d5db;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#dbeafe']) {
    border-color: #bfdbfe;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#cffafe']) {
    border-color: #a5f3fc;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#d1fae5']) {
    border-color: #a7f3d0;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#fef08a']) {
    border-color: #fde047;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#fce7f3']) {
    border-color: #f9a8d4;
}

:deep(.comment-toolbar .ql-picker.ql-color .ql-picker-options .ql-picker-item[data-value='#ede9fe']) {
    border-color: #c4b5fd;
}
</style>
